import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Search, Sparkles, Wand2 } from 'lucide-react';
import { format } from 'date-fns';
import type { CycleRing } from '@/lib/cycleTypes';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

/* ---------------- helpers ---------------- */

const getActiveStage = (ring: CycleRing) => {
  if (typeof ring.currentStage === 'number') return ring.currentStage;
  if (typeof ring.rotation === 'number' && ring.stages > 0) {
    const segmentAngle = 360 / ring.stages;
    const normalizedRotation = (((-ring.rotation) % 360) + 360) % 360;
    return Math.floor(normalizedRotation / segmentAngle + 1e-6) % ring.stages;
  }
  return 0;
};

const getPhaseName = (phase: number) => {
  if (phase < 0.25) return 'Rising';
  if (phase < 0.5) return 'Peak';
  if (phase < 0.75) return 'Integrating';
  return 'Resting';
};

const getPhaseScore = (phase: number) => {
  if (phase < 0.25) return 0.85;
  if (phase < 0.5) return 1;
  if (phase < 0.75) return 0.7;
  return 0.45;
};

const getRingPhilosophy = (ring: CycleRing) => {
  const ringName = ring.name ?? '';
  const key = `${ring.id} ${ringName}`.toLowerCase();

  if (/sun|solar/.test(key)) return { archetype: 'Vitality & Presence', strengths: 'visibility, courage', caution: 'avoid burnout' };
  if (/moon|lunar/.test(key)) return { archetype: 'Emotions & Intuition', strengths: 'reflection, empathy', caution: 'avoid rushing feelings' };
  if (/mercury/.test(key)) return { archetype: 'Communication & Clarity', strengths: 'learning, messaging', caution: 'double-check details' };
  if (/venus/.test(key)) return { archetype: 'Harmony & Connection', strengths: 'relationships, beauty', caution: 'avoid people-pleasing' };
  if (/mars/.test(key)) return { archetype: 'Action & Bravery', strengths: 'momentum, courage', caution: 'watch impatience' };
  if (/jupiter/.test(key)) return { archetype: 'Growth & Prosperity', strengths: 'expansion, optimism', caution: 'avoid excess' };
  if (/saturn/.test(key)) return { archetype: 'Structure & Discipline', strengths: 'planning, focus', caution: 'avoid rigidity' };

  return { archetype: 'Custom Rhythm', strengths: 'personal alignment', caution: 'keep intent clear' };
};

/* ---------------- types ---------------- */

type RingInsight = {
  id: string;
  name: string;
  stageLabel: string;
  phaseName: string;
  phaseScore: number;
  retrograde: boolean;
  philosophy: ReturnType<typeof getRingPhilosophy>;
};

interface AiDaySummaryWidgetProps {
  rings: CycleRing[];
  hiddenRings?: string[];
  visibleCount?: number | null;
  currentDate: Date;
  className?: string;
}

type AiProvider = 'openai' | 'google';
type ReadingSection = { title: string; body: string };
type WizardStep = 'query' | 'generate' | 'result';

const OPENAI_API_KEY_STORAGE = 'appsations.openai.apiKey';
const OPENAI_MODEL_STORAGE = 'appsations.openai.model';
const DEFAULT_OPENAI_MODEL = 'gpt-4o-mini';
const GOOGLE_AI_API_KEY_STORAGE = 'appsations.googleai.apiKey';
const GOOGLE_AI_MODEL_STORAGE = 'appsations.googleai.model';
const AI_PROVIDER_STORAGE = 'appsations.ai.provider';
const DEFAULT_GOOGLE_MODEL = 'gemini-2.5-flash';

const getStoredOpenAiApiKey = () => {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem(OPENAI_API_KEY_STORAGE) ?? '';
  } catch {
    return '';
  }
};

const getStoredOpenAiModel = () => {
  if (typeof window === 'undefined') return DEFAULT_OPENAI_MODEL;
  try {
    return window.localStorage.getItem(OPENAI_MODEL_STORAGE) ?? DEFAULT_OPENAI_MODEL;
  } catch {
    return DEFAULT_OPENAI_MODEL;
  }
};

const getStoredGoogleApiKey = () => {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem(GOOGLE_AI_API_KEY_STORAGE) ?? '';
  } catch {
    return '';
  }
};

const getStoredGoogleModel = () => {
  if (typeof window === 'undefined') return DEFAULT_GOOGLE_MODEL;
  try {
    return window.localStorage.getItem(GOOGLE_AI_MODEL_STORAGE) ?? DEFAULT_GOOGLE_MODEL;
  } catch {
    return DEFAULT_GOOGLE_MODEL;
  }
};

const getStoredProvider = (): AiProvider => {
  if (typeof window === 'undefined') return 'openai';
  try {
    const value = window.localStorage.getItem(AI_PROVIDER_STORAGE);
    return value === 'google' ? 'google' : 'openai';
  } catch {
    return 'openai';
  }
};

const normalizeGoogleModelName = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return DEFAULT_GOOGLE_MODEL;
  return trimmed.replace(/^models\//i, '');
};

const GOOGLE_FALLBACK_MODELS = ['gemini-2.5-flash', 'gemini-2.5-flash-lite', 'gemini-1.5-flash-latest'];

const isUnsupportedGoogleGenerateContentModel = (message: string) =>
  /not found for api version|not supported for generatecontent/i.test(message);

const parseReadingSections = (value: string): ReadingSection[] => {
  const text = value.trim();
  if (!text) return [];

  const lines = text.split(/\r?\n/);
  const sections: ReadingSection[] = [];
  let currentTitle = 'Reading';
  let currentBody: string[] = [];

  const commit = () => {
    const body = currentBody.join('\n').trim();
    if (body) {
      sections.push({ title: currentTitle, body });
    }
    currentBody = [];
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    const headingMatch = line.match(/^\*\*\s*(\d+\)\s*[^*]+?)\s*\*\*\s*$/) ?? line.match(/^(\d+\)\s*.+)$/);
    if (headingMatch) {
      commit();
      currentTitle = headingMatch[1].trim();
      continue;
    }
    currentBody.push(rawLine);
  }

  commit();
  if (sections.length) return sections;
  return [{ title: 'Reading', body: text }];
};

/* ---------------- component ---------------- */

export function AiDaySummaryWidget({
  rings,
  hiddenRings = [],
  visibleCount,
  currentDate,
  className,
}: AiDaySummaryWidgetProps) {
  const [reading, setReading] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showApiDialog, setShowApiDialog] = useState(false);
  const [provider, setProvider] = useState<AiProvider>(() => getStoredProvider());
  const [storedApiKey, setStoredApiKey] = useState(() => getStoredOpenAiApiKey());
  const [storedModel, setStoredModel] = useState(() => getStoredOpenAiModel());
  const [storedGoogleApiKey, setStoredGoogleApiKey] = useState(() => getStoredGoogleApiKey());
  const [storedGoogleModel, setStoredGoogleModel] = useState(() => getStoredGoogleModel());
  const [draftApiKey, setDraftApiKey] = useState('');
  const [draftModel, setDraftModel] = useState(DEFAULT_OPENAI_MODEL);
  const [readingFontSize, setReadingFontSize] = useState(18);
  const [showReadingOverlay, setShowReadingOverlay] = useState(false);
  const [showEventSearch, setShowEventSearch] = useState(false);
  const [eventQuery, setEventQuery] = useState('');
  const [lookAheadDays, setLookAheadDays] = useState(14);
  const [searchOnlyMode, setSearchOnlyMode] = useState(false);
  const [wizardStep, setWizardStep] = useState<WizardStep>('query');
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);

  useEffect(() => {
    const refreshFromStorage = () => {
      setProvider(getStoredProvider());
      setStoredApiKey(getStoredOpenAiApiKey());
      setStoredModel(getStoredOpenAiModel());
      setStoredGoogleApiKey(getStoredGoogleApiKey());
      setStoredGoogleModel(getStoredGoogleModel());
    };

    refreshFromStorage();
    window.addEventListener('storage', refreshFromStorage);
    window.addEventListener('openai-settings-changed', refreshFromStorage as EventListener);
    return () => {
      window.removeEventListener('storage', refreshFromStorage);
      window.removeEventListener('openai-settings-changed', refreshFromStorage as EventListener);
    };
  }, []);

  const visibleRings = useMemo(() => {
    const safeRings = Array.isArray(rings) ? rings.filter((ring) => ring && ring.id) : [];
    const filtered = hiddenRings.length
      ? safeRings.filter(r => !hiddenRings.includes(r.id))
      : safeRings;

    return typeof visibleCount === 'number'
      ? filtered.slice(0, visibleCount)
      : filtered;
  }, [rings, hiddenRings, visibleCount]);

  const ringInsights = useMemo<RingInsight[]>(() => {
    return visibleRings
      .filter(ring => ring?.id && ring?.name) // Guard: only process rings with id and name
      .map(ring => {
        const stage = getActiveStage(ring);
        const phase = stage / Math.max(ring.stages || 1, 1);

        return {
          id: ring.id,
          name: ring.name ?? 'Unnamed Cycle',
          stageLabel: String(stage + 1),
          phaseName: getPhaseName(phase),
          phaseScore: getPhaseScore(phase),
          retrograde: !!ring.retrograde,
          philosophy: getRingPhilosophy(ring),
        };
      });
  }, [visibleRings]);

  const summary = useMemo(() => {
    // Double-guard: filter out any insights without a name
    const validInsights = ringInsights.filter(r => r.name && r.phaseName);
    const sorted = [...validInsights].sort((a, b) => b.phaseScore - a.phaseScore);
    const top = sorted.slice(0, 2);
    const retro = validInsights.filter(r => r.retrograde);

    return {
      headline: `Today's strongest alignment: ${top.map(r => `${r.name ?? 'Unknown'} (${r.phaseName ?? 'phase'})`).join(' + ')}`,
      details: retro.length
        ? `Retrograde cues in ${retro.map(r => (r.name ?? 'Unknown')).join(', ')} — proceed gently.`
        : 'Steady, grounded pacing is favored today.',
    };
  }, [ringInsights]);

  const intentRows = useMemo(() => {
    const intents = [
      { label: 'Dentist visit', targets: ['mars', 'sun', 'moon'], description: 'Courage with calm.' },
      { label: 'Financial meeting', targets: ['jupiter', 'saturn', 'mercury'], description: 'Expansion with structure.' },
      { label: 'Heart-to-heart', targets: ['venus', 'moon', 'mercury'], description: 'Emotional clarity.' },
    ];

    return intents.map(intent => {
      const matched = intent.targets
        .map(t => ringInsights.find(r => r && r.id && r.id.toLowerCase().includes(t)))
        .filter((r): r is RingInsight => r !== undefined && r !== null);

      const avg = matched.length
        ? matched.reduce((a, r) => a + r.phaseScore, 0) / matched.length
        : 0;

      const retro = matched.some(r => r.retrograde);
      const score = retro ? avg - 0.35 : avg;

      return {
        ...intent,
        window: matched.map(r => `${r.name ?? 'Unknown'} · ${r.phaseName ?? 'phase'}`).join(' • ') || '—',
        signal: retro ? 'Caution' : score > 0.78 ? 'Strong' : score > 0.6 ? 'Steady' : 'Gentle',
        retrograde: retro,
      };
    });
  }, [ringInsights]);

  const buildPrompt = useMemo(() => {
    const dateLabel = currentDate && !isNaN(currentDate.getTime())
      ? format(currentDate, 'EEEE, MMM d yyyy HH:mm')
      : 'unknown date';

    const cycleLines = ringInsights.length
      ? ringInsights.map((insight) => `${insight.name}: ${insight.phaseName}, stage ${insight.stageLabel}${insight.retrograde ? ', retrograde' : ''}`)
      : ['No visible cycle data'];

    const timingLines = intentRows.map((row) => `${row.label}: ${row.signal} (${row.window})`);
    const normalizedEventQuery = eventQuery.trim();
    const eventTokens = normalizedEventQuery
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .map((token) => token.trim())
      .filter((token) => token.length >= 3);

    const matchedRings = eventTokens.length
      ? visibleRings.filter((ring) => {
          const key = `${ring.id} ${ring.name ?? ''}`.toLowerCase();
          return eventTokens.some((token) => key.includes(token));
        })
      : [];

    const forecastRings = matchedRings.length > 0 ? matchedRings : visibleRings;
    const safeLookAheadDays = Math.max(1, Math.min(90, lookAheadDays));
    const eventForecastLines = normalizedEventQuery
      ? Array.from({ length: safeLookAheadDays }, (_, offset) => {
          const date = new Date(currentDate);
          date.setDate(currentDate.getDate() + offset);

          const ringSnapshots = forecastRings.map((ring) => {
            const stages = Math.max(ring.stages || 1, 1);
            const currentStage = getActiveStage(ring);
            const futureStage = (currentStage + offset) % stages;
            const phase = futureStage / stages;
            return {
              name: ring.name,
              phaseName: getPhaseName(phase),
              phaseScore: getPhaseScore(phase),
            };
          });

          const averageScore = ringSnapshots.length
            ? ringSnapshots.reduce((sum, item) => sum + item.phaseScore, 0) / ringSnapshots.length
            : 0;

          const topTwo = [...ringSnapshots]
            .sort((a, b) => b.phaseScore - a.phaseScore)
            .slice(0, 2)
            .map((item) => `${item.name}(${item.phaseName})`)
            .join(' + ');

          return `${format(date, 'EEE, MMM d')}: score ${averageScore.toFixed(2)}${topTwo ? ` | top: ${topTwo}` : ''}`;
        })
      : [];

    const eventFocusLines = normalizedEventQuery
      ? [
          'User event focus:',
          normalizedEventQuery,
          'Prioritize timing, synergy, and cautions for this event focus.',
          '14-day event forecast context:',
          ...eventForecastLines,
          'Recommend up to 3 best days from the 14-day window for this event focus.',
          'For each recommended day, include: date, short reason, and confidence (Strong/Steady/Gentle).',
        ]
      : [];

    if (normalizedEventQuery && searchOnlyMode) {
      return [
        `Date: ${dateLabel}`,
        `Event search focus: ${normalizedEventQuery}`,
        `Look-ahead window: ${safeLookAheadDays} day(s)`,
        'Cycle snapshot:',
        ...cycleLines,
        `${safeLookAheadDays}-day event forecast context:`,
        ...eventForecastLines,
        'Output ONLY this format:',
        'Recommended Day 1: <Weekday, Mon D> | Confidence: <Strong/Steady/Gentle> | Reason: <short reason>',
        'Recommended Day 2: <Weekday, Mon D> | Confidence: <Strong/Steady/Gentle> | Reason: <short reason>',
        'Recommended Day 3: <Weekday, Mon D> | Confidence: <Strong/Steady/Gentle> | Reason: <short reason>',
        'If fewer than 3 are strong, provide only the strong options.',
      ].join('\n');
    }

    return [
      `Date: ${dateLabel}`,
      `Visible cycles: ${cycleLines.length}`,
      'Cycle snapshot:',
      ...cycleLines,
      'Intent timing snapshot:',
      ...timingLines,
      ...eventFocusLines,
      'Give a concise day reading with these sections:',
      '1) Overall energy',
      '2) Best windows for action',
      '3) Cautions',
      '4) One practical ritual for today',
      ...(normalizedEventQuery ? ['5) Recommended day(s) for this event focus (must include specific dates + supporting reasons)'] : []),
    ].join('\n');
  }, [currentDate, eventQuery, intentRows, lookAheadDays, ringInsights, searchOnlyMode, visibleRings]);

  const readingSections = useMemo(() => parseReadingSections(reading), [reading]);
  const displayedSections = useMemo(() => {
    if (searchOnlyMode && reading.trim()) {
      return [{ title: 'Recommended Day(s)', body: reading.trim() }];
    }
    return readingSections;
  }, [reading, readingSections, searchOnlyMode]);
  const activeSection = displayedSections[activeSectionIndex] ?? null;

  useEffect(() => {
    setActiveSectionIndex(0);
    if (displayedSections.length > 0) {
      setWizardStep('result');
    }
  }, [displayedSections.length]);

  const requestReading = async () => {
    setError('');
    const envOpenAiKey = ((import.meta as any)?.env?.VITE_OPENAI_API_KEY ?? '') as string;
    const envGoogleKey = ((import.meta as any)?.env?.VITE_GOOGLE_AI_API_KEY ?? '') as string;

    const apiKey = provider === 'google'
      ? (storedGoogleApiKey || envGoogleKey || '')
      : (storedApiKey || envOpenAiKey || '');

    const selectedModel = provider === 'google'
      ? normalizeGoogleModelName(storedGoogleModel || DEFAULT_GOOGLE_MODEL)
      : (storedModel || DEFAULT_OPENAI_MODEL);

    if (!apiKey) {
      setError(provider === 'google'
        ? 'Google AI API key not configured. Add it from the API badge setup dialog.'
        : 'OpenAI API key not configured. Add it from the API badge setup dialog.');
      setDraftApiKey(provider === 'google' ? storedGoogleApiKey : storedApiKey);
      setDraftModel(provider === 'google' ? (storedGoogleModel || DEFAULT_GOOGLE_MODEL) : (storedModel || DEFAULT_OPENAI_MODEL));
      setShowApiDialog(true);
      return;
    }

    setWizardStep('generate');
    setLoading(true);
    try {
      let resolvedGoogleModel = selectedModel;
      let response = provider === 'google'
        ? null
        : await fetch('https://api.openai.com/v1/chat/completions', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${apiKey}`,
            },
            body: JSON.stringify({
              model: selectedModel,
              temperature: 0.7,
              messages: [
                {
                  role: 'system',
                  content: 'You are a concise and grounded calendar analyst. Keep the response clear and practical.',
                },
                {
                  role: 'user',
                  content: buildPrompt,
                },
              ],
            }),
          });

      if (provider === 'google') {
        const modelsToTry = [
          selectedModel,
          ...GOOGLE_FALLBACK_MODELS.filter((m) => m !== selectedModel),
        ];

        for (let i = 0; i < modelsToTry.length; i += 1) {
          const model = modelsToTry[i];
          const candidateResponse = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(model)}:generateContent?key=${encodeURIComponent(apiKey)}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              contents: [
                {
                  role: 'user',
                  parts: [{ text: buildPrompt }],
                },
              ],
              generationConfig: {
                temperature: 0.7,
              },
            }),
          });

          if (candidateResponse.ok) {
            response = candidateResponse;
            resolvedGoogleModel = model;
            break;
          }

          const errorBody = await candidateResponse.text();
          let parsedMessage = '';
          try {
            const parsed = JSON.parse(errorBody);
            parsedMessage = typeof parsed?.error?.message === 'string' ? parsed.error.message : '';
          } catch {
            parsedMessage = errorBody;
          }

          const isLastAttempt = i === modelsToTry.length - 1;
          if (!isUnsupportedGoogleGenerateContentModel(parsedMessage) || isLastAttempt) {
            response = new Response(errorBody, {
              status: candidateResponse.status,
              statusText: candidateResponse.statusText,
              headers: candidateResponse.headers,
            });
            break;
          }
        }
      }

      if (!response) {
        throw new Error('Unable to generate reading right now.');
      }

      if (!response.ok) {
        const body = await response.text();
        let message = `${provider === 'google' ? 'Google AI' : 'OpenAI'} request failed (${response.status}).`;
        try {
          const parsed = JSON.parse(body);
          const apiError = parsed?.error;
          const apiMessage = typeof apiError?.message === 'string' ? apiError.message : '';
          const apiCode = typeof apiError?.code === 'string' ? apiError.code : '';
          const apiType = typeof apiError?.type === 'string' ? apiError.type : '';

          if (apiCode === 'insufficient_quota' || apiType === 'insufficient_quota') {
            message = 'OpenAI quota exceeded. Please check billing/credits, then try again. https://platform.openai.com/settings/organization/billing';
          } else if (provider === 'google' && /not found for api version|not supported for generatecontent/i.test(apiMessage)) {
            message = `Google model \"${selectedModel}\" is unavailable for generateContent. Try: gemini-2.5-flash, gemini-2.5-flash-lite, or gemini-1.5-flash-latest. List models: https://generativelanguage.googleapis.com/v1beta/models?key=YOUR_API_KEY`;
          } else if (apiMessage) {
            message = apiMessage;
          }
        } catch {
          if (body && !body.trim().startsWith('{')) {
            message = body;
          }
        }
        throw new Error(message);
      }

      const data = await response.json();
      const text = provider === 'google'
        ? (Array.isArray(data?.candidates)
            ? String(
                data.candidates?.[0]?.content?.parts
                  ?.map((part: any) => (typeof part?.text === 'string' ? part.text : ''))
                  ?.join('\n') ?? ''
              ).trim()
            : '')
        : (typeof data?.choices?.[0]?.message?.content === 'string'
            ? data.choices[0].message.content.trim()
            : '');

      if (provider === 'google' && resolvedGoogleModel !== selectedModel && typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(GOOGLE_AI_MODEL_STORAGE, resolvedGoogleModel);
        } catch {
          // Ignore storage-restricted environments.
        }
        setStoredGoogleModel(resolvedGoogleModel);
      }

      setReading(text || 'No reading returned from the model.');
      setWizardStep('result');
    } catch (err) {
      const rawMessage = err instanceof Error ? err.message : 'Unable to generate reading right now.';
      const message = rawMessage.trim().startsWith('{')
        ? `Unable to generate reading right now. Please verify your ${provider === 'google' ? 'Google AI' : 'OpenAI'} key, model, and billing status.`
        : rawMessage;
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const saveApiSettings = () => {
    if (typeof window === 'undefined') return;
    const nextKey = draftApiKey.trim();
    const nextModel = provider === 'google'
      ? normalizeGoogleModelName(draftModel)
      : (draftModel.trim() || DEFAULT_OPENAI_MODEL);

    try {
      if (provider === 'google') {
        if (nextKey) {
          window.localStorage.setItem(GOOGLE_AI_API_KEY_STORAGE, nextKey);
        } else {
          window.localStorage.removeItem(GOOGLE_AI_API_KEY_STORAGE);
        }
        window.localStorage.setItem(GOOGLE_AI_MODEL_STORAGE, nextModel);
      } else {
        if (nextKey) {
          window.localStorage.setItem(OPENAI_API_KEY_STORAGE, nextKey);
        } else {
          window.localStorage.removeItem(OPENAI_API_KEY_STORAGE);
        }
        window.localStorage.setItem(OPENAI_MODEL_STORAGE, nextModel);
      }
      window.localStorage.setItem(AI_PROVIDER_STORAGE, provider);
    } catch {
      // Ignore storage-restricted environments.
    }

    window.dispatchEvent(new Event('openai-settings-changed'));
    if (provider === 'google') {
      setStoredGoogleApiKey(nextKey);
      setStoredGoogleModel(nextModel);
    } else {
      setStoredApiKey(nextKey);
      setStoredModel(nextModel);
    }
    setError('');
    setShowApiDialog(false);
  };

  return (
    <div className={cn('space-y-4 font-sans', className)}>
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-black/30 backdrop-blur-md p-4 text-white shadow-lg">
        <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br from-violet-900/20 via-transparent to-indigo-900/20" />

        <div className="relative">
          <div className="flex items-center gap-2 text-base font-semibold text-white mb-0.5">
            <Sparkles className="w-4 h-4 text-violet-300" />
            AI · Day Alignment
          </div>
          <div className="text-sm text-white/50 mb-3">
            {currentDate && !isNaN(currentDate.getTime()) ? format(currentDate, 'EEEE, MMM d · HH:mm') : '—'}
          </div>

          <div className="flex items-center justify-center">
            <Button
              type="button"
              variant={showEventSearch ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowEventSearch(true)}
              className="border-white/20 text-white/80 hover:bg-white/10 hover:text-white"
            >
              <Search className="mr-1.5 h-4 w-4" />
              Search Event
            </Button>
          </div>

          {error ? <p className="mt-3 text-sm text-red-400">{error}</p> : null}

          {readingSections.length > 0 ? (
            <div className="mt-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white/50">Text size</span>
                  <input
                    type="range"
                    min={14}
                    max={30}
                    step={1}
                    value={readingFontSize}
                    onChange={(event) => setReadingFontSize(Number(event.target.value))}
                    className="h-2 w-36"
                    aria-label="Reading text size"
                  />
                  <span className="text-xs text-white/50 bg-white/10 rounded px-1.5 py-0.5">{readingFontSize}px</span>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowReadingOverlay(true)}
                  className="border-white/20 text-white/70 hover:bg-white/10 hover:text-white">
                  Full Screen
                </Button>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                {readingSections.map((section, index) => (
                  <div key={`${section.title}-${index}`} className="rounded-xl bg-white/5 border border-white/10 p-3">
                    <div className="text-sm font-semibold text-white/80 mb-2">{section.title}</div>
                    <div
                      className="max-h-52 overflow-y-auto whitespace-pre-wrap leading-relaxed text-white/70"
                      style={{ fontSize: `${readingFontSize}px` }}
                    >
                      {section.body}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <Dialog open={showEventSearch} onOpenChange={setShowEventSearch}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-4 w-4" />
              Search Event
            </DialogTitle>
            <DialogDescription>
              Enter one event or a combo of events to find the best aligned day.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label htmlFor="ai-event-query-dialog" className="mb-1 block text-sm font-medium">
                Event or combo of events
              </label>
              <input
                id="ai-event-query-dialog"
                type="text"
                value={eventQuery}
                onChange={(event) => setEventQuery(event.target.value)}
                placeholder="Ex: dentist + financial meeting + travel"
                className="w-full rounded-md border border-border/60 bg-background px-3 py-2 text-sm"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Tip: combine events with + or commas for blended guidance.
              </p>
            </div>
            <div>
              <label htmlFor="ai-look-ahead-days" className="mb-1 block text-sm font-medium">
                How far ahead to search (days)
              </label>
              <input
                id="ai-look-ahead-days"
                type="number"
                min={1}
                max={90}
                value={lookAheadDays}
                onChange={(event) => {
                  const parsed = Number(event.target.value);
                  if (Number.isFinite(parsed)) {
                    setLookAheadDays(Math.max(1, Math.min(90, Math.floor(parsed))));
                  }
                }}
                className="w-full rounded-md border border-border/60 bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setShowEventSearch(false)}>
                Close
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setSearchOnlyMode(true);
                  setShowEventSearch(false);
                  void requestReading();
                }}
                disabled={loading}
              >
                {loading ? 'Searching...' : 'Find Best Day(s)'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showReadingOverlay} onOpenChange={setShowReadingOverlay}>
        <DialogContent className="h-[92vh] w-[96vw] max-w-[96vw] p-0 bg-black/80 backdrop-blur-xl border-white/10 text-white">
          <div className="flex h-full flex-col">
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <div>
                <p className="text-sm text-white/50">AI Day Reading</p>
                <h3 className="text-lg font-semibold text-white">Full Screen Reading</h3>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-white/50">Text size</span>
                <input
                  type="range"
                  min={14}
                  max={36}
                  step={1}
                  value={readingFontSize}
                  onChange={(event) => setReadingFontSize(Number(event.target.value))}
                  className="h-2 w-40"
                  aria-label="Full screen reading text size"
                />
                <span className="text-xs text-white/50 bg-white/10 rounded px-1.5 py-0.5">{readingFontSize}px</span>
              </div>
            </div>

            <div className="grid flex-1 gap-3 overflow-y-auto p-4 md:grid-cols-2">
              {readingSections.map((section, index) => (
                <div key={`overlay-${section.title}-${index}`} className="rounded-xl bg-white/5 border border-white/10 p-3">
                  <div className="text-base font-semibold text-white/80 mb-2 md:text-lg">{section.title}</div>
                  <div
                    className="max-h-[56vh] overflow-y-auto whitespace-pre-wrap leading-relaxed text-white/70"
                    style={{ fontSize: `${readingFontSize}px` }}
                  >
                    {section.body}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showApiDialog} onOpenChange={setShowApiDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>AI API Setup</DialogTitle>
            <DialogDescription>
              Choose provider and add API credentials to enable AI day readings.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Provider</label>
              <div className="mt-1 flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={provider === 'openai' ? 'default' : 'outline'}
                  onClick={() => {
                    setProvider('openai');
                    setDraftApiKey(storedApiKey);
                    setDraftModel(storedModel || DEFAULT_OPENAI_MODEL);
                    if (typeof window !== 'undefined') {
                      try { window.localStorage.setItem(AI_PROVIDER_STORAGE, 'openai'); } catch {}
                    }
                  }}
                >
                  OpenAI
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={provider === 'google' ? 'default' : 'outline'}
                  onClick={() => {
                    setProvider('google');
                    setDraftApiKey(storedGoogleApiKey);
                    setDraftModel(storedGoogleModel || DEFAULT_GOOGLE_MODEL);
                    if (typeof window !== 'undefined') {
                      try { window.localStorage.setItem(AI_PROVIDER_STORAGE, 'google'); } catch {}
                    }
                  }}
                >
                  Google AI
                </Button>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">{provider === 'google' ? 'Google AI API Key' : 'OpenAI API Key'}</label>
              <input
                type="password"
                value={draftApiKey}
                onChange={(event) => setDraftApiKey(event.target.value)}
                placeholder="sk-..."
                className="mt-1 w-full rounded-md border border-border/60 bg-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Model</label>
              <input
                type="text"
                value={draftModel}
                onChange={(event) => setDraftModel(event.target.value)}
                placeholder={DEFAULT_OPENAI_MODEL}
                className="mt-1 w-full rounded-md border border-border/60 bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <a
                href={provider === 'google' ? 'https://aistudio.google.com/app/apikey' : 'https://platform.openai.com/api-keys'}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-primary underline-offset-4 hover:underline"
              >
                {provider === 'google' ? 'Open Google AI API Keys' : 'Open OpenAI API Keys'}
              </a>
              <Button type="button" size="sm" onClick={saveApiSettings}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
